import { useState, useContext, useEffect } from 'react';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Mail, Lock, Shield, AlertCircle, CheckCircle2, Clock, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { normalizeApiError } from '../utils/normalizeError';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';

const AccountSettings = () => {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Email change state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState(null);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Unsaved-changes protection for the inline email/password sub-forms.
  const isDirty = Boolean(
    (isEditingEmail && (newEmail || emailPassword)) ||
    (showPasswordSection && (currentPassword || newPassword || confirmPassword)) ||
    deleteConfirmText
  );
  const { promptOpen, confirmLeave, cancelLeave, guardedLeave } = useUnsavedChangesGuard({
    active: isDirty,
    shouldPrompt: isDirty,
  });

  useEffect(() => {
    if (user) {
      checkPendingEmailChange();
    }
  }, [user]);

  const checkPendingEmailChange = async () => {
    try {
      const response = await apiClient.get('/users/email/pending-change');
      if (response.data.has_pending) {
        setPendingEmailChange(response.data);
      } else {
        setPendingEmailChange(null);
      }
    } catch (error) {
      console.error('Error checking pending email change:', error);
    }
  };

  const handleEmailChangeRequest = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      toast.error('Please enter a new email address');
      return;
    }
    
    if (!emailPassword) {
      toast.error('Please enter your current password');
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmittingEmail(true);
    
    try {
      const response = await apiClient.post('/users/email/change-request', {
        new_email: newEmail,
        current_password: emailPassword
      });
      
      toast.success(response.data.message || 'Verification email sent! Check your new email address.');
      
      // Clear form
      setNewEmail('');
      setEmailPassword('');
      setIsEditingEmail(false);
      
      // Refresh pending status
      await checkPendingEmailChange();
    } catch (error) {
      const errorMsg = normalizeApiError(error);
      toast.error(errorMsg || 'Failed to request email change');
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      await apiClient.post('/users/email/cancel-change');
      toast.success('Email change request cancelled');
      setPendingEmailChange(null);
    } catch (error) {
      toast.error('Failed to cancel email change');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully');
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      const errorMsg = normalizeApiError(error);
      toast.error(errorMsg || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Defensive: typed-confirmation must match before we even call the API.
    if (!user?.username || deleteConfirmText !== user.username) {
      return;
    }

    setIsDeletingAccount(true);
    try {
      // Backend cascades all related data and returns 204 No Content.
      await apiClient.delete('/auth/me');

      // Locally clear auth state. The user's refresh_token was already
      // removed by the backend cascade, so logout()'s call to /auth/logout
      // may 401 — that's expected and handled gracefully by logout() itself
      // (App.js wraps it in try/catch with a warning log only).
      try {
        await logout();
      } catch (_) {
        // Ignore — already cleared on the backend.
      }

      // Belt-and-suspenders local cleanup (logout() already does this,
      // but in case anything threw above):
      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('pendingRunId');
        localStorage.removeItem('pendingClubId');
      } catch (_) { /* noop */ }

      setShowDeleteDialog(false);
      toast.success('Your account has been deleted');
      navigate('/', { replace: true });
    } catch (error) {
      setIsDeletingAccount(false);
      const errorMsg = normalizeApiError(error);
      toast.error(errorMsg || 'Failed to delete account. Please try again or contact support.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => guardedLeave(() => navigate('/profile'))}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Profile
          </Button>
          <h1 className="text-xl font-semibold">Account Settings</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Info Alert */}
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <strong>Privacy:</strong> Your email is private and never shown to other users. 
            Your username is your public identity.
          </AlertDescription>
        </Alert>

        {/* My Run History entry */}
        <Card
          className="cursor-pointer hover:border-orange-300 transition-colors"
          onClick={() => navigate('/my-history')}
          data-testid="my-history-card"
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-orange-500" /> My Run History
            </CardTitle>
            <CardDescription>
              View and export your personal run/session history, status, and waitlist activity
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Public Identity Section */}
        <Card>
          <CardHeader>
            <CardTitle>Public Identity</CardTitle>
            <CardDescription>
              This is how other users see you on OpenRuns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Username (public)</Label>
              <Input
                value={user.username}
                disabled
                className="bg-gray-50"
              />
              <p className="text-sm text-gray-500">
                Your username cannot be changed. Contact support if you need assistance.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email Management Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Address (private)
            </CardTitle>
            <CardDescription>
              Used for login, notifications, and account recovery
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Email */}
            <div className="space-y-2">
              <Label>Current Email</Label>
              <Input
                value={user.email}
                disabled
                className="bg-gray-50"
              />
            </div>

            {/* Pending Change Alert */}
            {pendingEmailChange && (
              <Alert className="border-blue-200 bg-blue-50">
                <Clock className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium text-blue-900">
                      Email change pending verification
                    </p>
                    <p className="text-sm text-blue-800">
                      We sent a verification link to <strong>{pendingEmailChange.new_email}</strong>
                    </p>
                    <p className="text-sm text-blue-700">
                      Check your new email and click the verification link to complete the change.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEmailChange}
                      className="mt-2"
                    >
                      Cancel Request
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Email Change Form */}
            {!pendingEmailChange && (
              <>
                {!isEditingEmail ? (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditingEmail(true)}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Change Email Address
                  </Button>
                ) : (
                  <form onSubmit={handleEmailChangeRequest} className="space-y-4 p-4 border rounded-lg bg-gray-50">
                    <div className="space-y-2">
                      <Label htmlFor="newEmail">New Email Address</Label>
                      <Input
                        id="newEmail"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="your.new.email@example.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emailPassword">Current Password (for security)</Label>
                      <Input
                        id="emailPassword"
                        type="password"
                        value={emailPassword}
                        onChange={(e) => setEmailPassword(e.target.value)}
                        placeholder="Enter your current password"
                        required
                      />
                    </div>

                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        We'll send a verification link to your new email address. 
                        Your current email will remain active until you verify the new one.
                      </AlertDescription>
                    </Alert>

                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={isSubmittingEmail}
                      >
                        {isSubmittingEmail ? 'Sending...' : 'Send Verification Email'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditingEmail(false);
                          setNewEmail('');
                          setEmailPassword('');
                        }}
                        disabled={isSubmittingEmail}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Password
            </CardTitle>
            <CardDescription>
              Change your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showPasswordSection ? (
              <Button
                variant="outline"
                onClick={() => setShowPasswordSection(true)}
                className="gap-2"
              >
                <Lock className="h-4 w-4" />
                Change Password
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <p className="text-sm text-gray-500">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={isChangingPassword}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* ════════════════════════ DANGER ZONE ════════════════════════ */}
        <Card className="border-red-200 bg-red-50/30" data-testid="danger-zone-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-red-700/80">
              Permanently delete your Open Runs USA account. This cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-700 mb-4">
              Deleting your account will permanently remove your profile, follows, comments,
              hosted runs, and notifications. Roster entries on other players' games will be
              removed, but the games themselves stay intact for the other players.
            </p>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteConfirmText('');
                setShowDeleteDialog(true);
              }}
              data-testid="open-delete-account-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Delete-account confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        if (!isDeletingAccount) {
          setShowDeleteDialog(open);
          if (!open) setDeleteConfirmText('');
        }
      }}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">
              Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-zinc-700">
                <p>
                  This action is <strong>permanent and cannot be undone</strong>.
                  Your account, profile, follows, comments, and any runs you have hosted
                  will be deleted immediately.
                </p>
                <p>
                  Roster entries on other players' games will be removed; the games
                  themselves will remain for the other players.
                </p>
                <p className="pt-2">
                  To confirm, type your username exactly:&nbsp;
                  <span className="font-mono font-semibold text-zinc-900">
                    {user?.username || ''}
                  </span>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-2">
            <Input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={user?.username || 'username'}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid="delete-account-confirm-input"
              disabled={isDeletingAccount}
              className="font-mono"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeletingAccount}
              data-testid="delete-account-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeletingAccount || deleteConfirmText !== (user?.username || '___')}
              data-testid="delete-account-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingAccount ? 'Deleting…' : 'Delete Forever'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DiscardChangesDialog
        open={promptOpen}
        onKeepEditing={cancelLeave}
        onDiscard={confirmLeave}
      />
    </div>
  );
};

export default AccountSettings;
AccountSettings;
